<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { ESSENTIALS, FREE } from "@/consts/bundles";
import { removeProgress } from "@/services/middlewareService/learnerProgress";
import { EventBus } from "@/store/eventBus";
import { urlContentTypes } from "@/utils/contentRemovable";
import isUnauth from "@/utils/isUnauth";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";

const props = defineProps<{
  result: any;
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
  isRemovable?: boolean;
  topicView?: boolean;
  isAdminContent?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}>();

const store = useStore();
const route = useRoute();
const router = useRouter();

// Computed properties
const inMyList = computed(() => store.getters["user/inMyList"]);
const freeId = computed(() => store.getters["content/freeId"]);

const freeContent = computed(() => {
  return props.result?.content_bundle?.includes(freeId.value);
});

const showPremium = computed(() => {
  return !props.status?.userEntitlements?.includes(props.result?.guid);
});

const resultLink = computed(() => {
  let pathId;
  if (props.result?.friendly_url?.length > 0) {
    pathId = props.result.friendly_url;
  } else {
    pathId = props.result.id;
  }

  if (!pathId) {
    return "";
  }
  return router.resolve({
    name: "Path",
    params: { pathId },
  }).href;
});

// Methods
const removeFromMyProgress = (content: any) => {
  store.commit("user/removeFromMyProgress", content);
};

const bookmarkMe = (content: any) => {
  toggleMyListItem(content, content.type, {
    inMyList: inMyList.value,
    addContentToMyList: (payload: any) =>
      store.dispatch("user/addContentToMyList", payload),
    removeContentFromMyList: (payload: any) =>
      store.dispatch("user/removeContentFromMyList", payload),
    route,
  });
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const openCartModal = () => {
  const cartContent = {
    data: [
      {
        id: props.result.id,
        name: props.result.name,
        guid: props.result.guid,
        acronym: props.result.acronym,
        type: props.result.type,
        display_version: props.result.display_version,
      },
    ],
    courseCard: false,
  };
  EventBus.emit("openCartModal", cartContent);
};

const sendTelemetry = async () => {
  const telemetryObject = {
    action: "launch-contentType",
    url: resultLink.value,
    title: props.result.title ? props.result.title : props.result.name,
    id: props.result.id,
    page: route.fullPath,
    type: "learning-path",
    relevance: props.result.relevance,
    sortOrder: props.index,
    accessObject: {} as any,
    assignedContent: undefined as any,
  };
  if (showPremium.value) {
    if (
      props.status?.bundles?.entitlements?.some(
        (entitlement: any) => entitlement.content_bundle_name === ESSENTIALS
      )
    ) {
      telemetryObject.accessObject.contentAccessLevel = ESSENTIALS;
    } else {
      telemetryObject.accessObject.contentAccessLevel = FREE;
    }

    telemetryObject.accessObject.type = "contentEntitlementAccess";
    telemetryObject.accessObject.status = "locked";
  }

  if (props.result.assigned_content) {
    telemetryObject.assignedContent = props.result.assigned_content?.isAssigned;
    telemetryObject.sortOrder = props.result.assigned_content?.index;
  }

  let telemetryResponse = telemetry.external(telemetryObject, isUnauth());
  telemetryResponse
    .then(() => {
      router.push(resultLink.value);
    })
    .catch(() => {
      // do nothing
    });
};

const sendRmTelemetry = async (content: any) => {
  const contentId = content.content_id || content.id || "";
  return await telemetry.remove_from_myLearning(
    window.location.origin + route.fullPath,
    `/${urlContentTypes(content.type)}/${contentId}`,
    content.title || content.name || "",
    content.type,
    contentId,
    isUnauth()
  );
};

const removeFromMyProgressList = async (content: any) => {
  removeProgress(content.guid, content.type)
    .then(() => {
      sendRmTelemetry(content);
      removeFromMyProgress(content);
    })
    .catch(() => {
      // do nothing
    });
};

// Expose methods for tests
defineExpose({
  bookmarkMe,
  debouncedBookmarkMe,
  openCartModal,
  sendTelemetry,
  sendRmTelemetry,
  removeFromMyProgressList,
  addContentToMyList: (payload: any) =>
    store.dispatch("user/addContentToMyList", payload),
  removeContentFromMyList: (payload: any) =>
    store.dispatch("user/removeContentFromMyList", payload),
  freeContent,
  showPremium,
  resultLink,
  result: computed(() => props.result),
});
</script>

<template>
  <div
    v-show="result"
    data-cy="path-card"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :link="resultLink"
      :status="status"
      :cy="'path-card-link'"
      :is-removable="isRemovable"
      :show-description="props.showDescription !== false"
      @navigate="sendTelemetry()"
      :is-admin-content="isAdminContent"
      @bookmark="debouncedBookmarkMe(result)"
      @cta-click="openCartModal()"
      @remove-progress="removeFromMyProgressList(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'path-product-card-link'"
      :is-removable="isRemovable"
      :link="resultLink"
      :show-description="props.showDescription !== false"
      @bookmark="debouncedBookmarkMe(result)"
      @cta-click="openCartModal()"
      @navigate="sendTelemetry()"
    />
  </div>
</template>
